import { usePacientes } from 'hooks/usePacientes';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import { getInfrastructureErrorCopy } from 'utils/apiErrors';
import DestinatarioSelect from './DestinatarioSelect';

// Helper: Nome do Funcionário no padrão SQL
function getNomeFuncionario(cod, funcionarios) {
  if (!cod) return '';
  const f = (funcionarios || []).find((func) => String(func.codFunc) === String(cod));
  return f ? f.nome : '';
}

// Helper: Hora no formato HHMM (Inteiro)
function getHoraEnvioInt() {
  const now = new Date();
  const h = now.getHours().toString().padStart(2, '0');
  const m = now.getMinutes().toString().padStart(2, '0');
  return parseInt(`${h}${m}`, 10);
}

// Helper: Formatação do Endereço real da API
function formatarEnderecoPaciente(pac) {
  if (!pac) return '';

  const rua = pac.ENDRESPAC || '';
  const bairro = pac.BAIRROPAC || '';
  const cidade = pac.CIDADEPAC || '';
  const estado = pac.ESTADOPAC || '';
  const cep = pac.CEPPAC || '';

  const partesLocal = [rua, bairro, cidade].filter(Boolean).join(', ');
  const parteEstado = estado ? (partesLocal ? `/${estado}` : estado) : '';
  const parteCep = cep ? ` - CEP: ${cep}` : '';

  return `${partesLocal}${parteEstado}${parteCep}`.trim();
}

const tiposPedido = [
  { label: 'RECEITA', value: 'RECEITA' },
  { label: 'ATESTADO', value: 'ATESTADO' },
  { label: 'PEDIDO DE EXAME', value: 'PEDIDO' },
  { label: 'RESULTADO DE EXAME', value: 'RESULTADO' },
  { label: 'RECADO', value: 'RECADO' },
  { label: 'OUTROS', value: 'OUTROS' },
];

const prioridades = [
  { label: 'NORMAL', value: 'NORMAL' },
  { label: 'URGENTE', value: 'URGENTE' },
];

/* const statusList = [
  { label: 'PENDENTE', value: 'PENDENTE' },
  { label: 'CONCLUIDO', value: 'CONCLUIDO' },
]; */

const MensagemForm = ({
  valores = {},
  onChange,
  onSubmit,
  destinatarios = [],
  editando = false,
  onFechar,
  codFuncLogado,
}) => {
  const [buscaPac, setBuscaPac] = useState('');
  const [mostrarDropPac, setMostrarDropPac] = useState(false);
  const [textoLivre, setTextoLivre] = useState(''); // Guarda apenas as observações/corpo extra
  const containerRef = useRef(null);

  const {
    pacientes = [],
    isLoading: buscandoPac,
    error: errorPacientes,
  } = usePacientes({
    nome: buscaPac,
  });

  // 1. Garante que a Origem Venha Preenchida com o Usuário Logado por Padrão
  useEffect(() => {
    if (!editando && !valores.CODFUNCORIGEM && codFuncLogado) {
      onChange({
        ...valores,
        CODFUNCORIGEM: String(codFuncLogado),
      });
    }
  }, [codFuncLogado]);

  // 2. Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setMostrarDropPac(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 3. Função Centralizada para Gerar o Cabeçalho da Mensagem Dinamicamente
  const recalcularMensagemCompleta = (novosValores, novoTextoLivre = textoLivre) => {
    const nomeOrigem =
      getNomeFuncionario(novosValores.CODFUNCORIGEM, destinatarios) || 'Não informada';
    const nomeDestino =
      getNomeFuncionario(novosValores.CODFUNCDESTINO, destinatarios) || 'Não informado';
    const nomePac = novosValores.NOMEPAC
      ? `${novosValores.NOMEPAC}${novosValores.CODPAC ? ` (Cód: ${novosValores.CODPAC})` : ''}`
      : 'Não selecionado';
    const endPac = novosValores.ENDERECOPAC || 'Não informado';

    const cabecalho =
      `De: ${nomeOrigem}\n` +
      `Para: ${nomeDestino}\n\n` +
      `Paciente: ${nomePac}\n` +
      `Endereço: ${endPac}\n` +
      `----------------------------------------\n\n`;

    return editando ? novoTextoLivre : `${cabecalho}${novoTextoLivre}`;
  };

  // Handler de Origem
  const handleOrigemChange = (e) => {
    const CODFUNCORIGEM = e.target.value;
    const novosValores = { ...valores, CODFUNCORIGEM };
    novosValores.MENSAGEM = recalcularMensagemCompleta(novosValores);
    onChange(novosValores);
  };

  // Handler de Destinatário
  const handleDestinatarioChange = (e) => {
    const CODFUNCDESTINO = e.target.value;
    const novosValores = { ...valores, CODFUNCDESTINO };
    novosValores.MENSAGEM = recalcularMensagemCompleta(novosValores);
    onChange(novosValores);
  };

  // Seleção do Paciente
  const handleSelecionarPaciente = (pac) => {
    if (!pac) return;
    const enderecoFormatado = formatarEnderecoPaciente(pac);

    const novosValores = {
      ...valores,
      CODPAC: String(pac.CODPAC || ''),
      NOMEPAC: pac.NOMEPAC || '',
      ENDERECOPAC: enderecoFormatado,
    };

    novosValores.MENSAGEM = recalcularMensagemCompleta(novosValores);
    onChange(novosValores);

    setBuscaPac('');
    setMostrarDropPac(false);
  };

  // Atualização da Mensagem Digitada pelo Operador
  /*   const handleTextoLivreChange = (e) => {
    const novoTexto = e.target.value;
    setTextoLivre(novoTexto);

    if (editando) {
      onChange({ ...valores, MENSAGEM: novoTexto });
    } else {
      // Se tiver em modo de criação, concatena com o cabeçalho dinâmico atual
      const mensagemCompleta = recalcularMensagemCompleta(valores, novoTexto);
      onChange({ ...valores, MENSAGEM: mensagemCompleta });
    }
  }; */

  // Submissão
  const handleSubmit = (e) => {
    e.preventDefault();
    const horaEnvio = getHoraEnvioInt();

    onSubmit({
      ...valores,
      HORA: horaEnvio,
      PRIORIDADE: valores.PRIORIDADE || 'NORMAL',
      STATUS: valores.STATUS || 'PENDENTE',
      TIPO_PEDIDO: valores.TIPO_PEDIDO || 'RECEITA',
    });
  };

  return (
    <Form onSubmit={handleSubmit} className="p-1">
      {/* SEÇÃO 1: ORIGEM E DESTINO */}
      <Card className="border-0 bg-light mb-3">
        <Card.Body className="p-3">
          <h6 className="text-primary fw-bold mb-3 small text-uppercase tracking-wide">
            1. Envio & Destino
          </h6>
          <Row className="g-3">
            <Col md={6} xs={12}>
              <Form.Group controlId="formOrigem">
                <Form.Label className="small fw-bold text-secondary">
                  Origem (Usuário Logado)
                </Form.Label>
                <DestinatarioSelect
                  destinatarios={destinatarios}
                  value={valores.CODFUNCORIGEM || ''}
                  onChange={handleOrigemChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6} xs={12}>
              <Form.Group controlId="formDestinatario">
                <Form.Label className="small fw-bold text-secondary">Destinatário</Form.Label>
                <DestinatarioSelect
                  destinatarios={destinatarios}
                  value={valores.CODFUNCDESTINO || ''}
                  onChange={handleDestinatarioChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* SEÇÃO 2: SELEÇÃO E DADOS DO PACIENTE */}
      <Card className="border-0 bg-light mb-3">
        <Card.Body className="p-3">
          <h6 className="text-primary fw-bold mb-3 small text-uppercase tracking-wide">
            2. Paciente Vinculado
          </h6>

          {!editando && (
            <Row className="g-3 mb-3">
              <Col xs={12}>
                <Form.Group
                  controlId="formBuscaPac"
                  className="position-relative"
                  ref={containerRef}
                >
                  <Form.Label className="small fw-bold text-secondary">
                    Pesquisar Paciente
                  </Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Digite o nome do paciente para buscar..."
                    value={buscaPac}
                    onChange={(e) => {
                      setBuscaPac(e.target.value);
                      setMostrarDropPac(true);
                    }}
                    onFocus={() => setMostrarDropPac(true)}
                    autoComplete="off"
                  />

                  {buscandoPac && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <small className="text-muted">Procurando pacientes...</small>
                    </div>
                  )}

                  {errorPacientes && !buscandoPac && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2">
                      <small>
                        {getInfrastructureErrorCopy(errorPacientes)?.title ||
                          'Erro ao buscar pacientes.'}
                      </small>
                    </Alert>
                  )}

                  {mostrarDropPac && pacientes.length > 0 && (
                    <ListGroup
                      className="position-absolute top-100 start-0 w-100 shadow-lg z-3 overflow-auto mt-1"
                      style={{ maxHeight: '220px' }}
                    >
                      {pacientes.map((pac) => {
                        const dtNasc =
                          pac.dtNasc || (pac.DTNPAC ? pac.DTNPAC.substring(0, 10) : '');
                        const celular = pac.CELULARPAC || pac.FONERESPAC || '';

                        return (
                          <ListGroup.Item
                            key={pac.CODPAC}
                            action
                            onClick={() => handleSelecionarPaciente(pac)}
                            className="py-2 px-3 border-bottom"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="fw-bold text-dark">{pac.NOMEPAC}</span>
                              <Badge bg={pac.STATUS === 'ATIVO' ? 'success' : 'secondary'}>
                                {pac.STATUS || 'ATIVO'}
                              </Badge>
                            </div>
                            <small className="text-muted d-block">
                              Cód: #{pac.CODPAC} {dtNasc ? `• Nasc: ${dtNasc}` : ''}{' '}
                              {celular ? `• Tel: ${celular}` : ''}
                            </small>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row className="g-3">
            <Col md={3} xs={12}>
              <Form.Group controlId="formPacienteCod">
                <Form.Label className="small fw-bold text-secondary">Cód. Paciente</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={valores.CODPAC || ''}
                  onChange={(e) => {
                    const novosValores = { ...valores, CODPAC: e.target.value };
                    novosValores.MENSAGEM = recalcularMensagemCompleta(novosValores);
                    onChange(novosValores);
                  }}
                  placeholder="Ex: 68301"
                />
              </Form.Group>
            </Col>
            <Col md={9} xs={12}>
              <Form.Group controlId="formPacienteNome">
                <Form.Label className="small fw-bold text-secondary">Nome do Paciente</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={valores.NOMEPAC || ''}
                  onChange={(e) => {
                    const novosValores = { ...valores, NOMEPAC: e.target.value };
                    novosValores.MENSAGEM = recalcularMensagemCompleta(novosValores);
                    onChange(novosValores);
                  }}
                  placeholder="Nome do paciente"
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId="formEndereco">
                <Form.Label className="small fw-bold text-secondary">Endereço Completo</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={valores.ENDERECOPAC || ''}
                  onChange={(e) => {
                    const novosValores = { ...valores, ENDERECOPAC: e.target.value };
                    novosValores.MENSAGEM = recalcularMensagemCompleta(novosValores);
                    onChange(novosValores);
                  }}
                  placeholder="Rua, Bairro, Cidade/UF - CEP"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* SEÇÃO 3: CLASSIFICAÇÃO */}
      <Row className="g-3 mb-3">
        <Col md={6} xs={12}>
          <Form.Group controlId="formTipoPedido">
            <Form.Label className="small fw-bold text-secondary">Tipo de Mensagem</Form.Label>
            <Form.Select
              size="sm"
              value={valores.TIPO_PEDIDO || 'RECEITA'}
              onChange={(e) => onChange({ ...valores, TIPO_PEDIDO: e.target.value })}
              required
            >
              {tiposPedido.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6} xs={12}>
          <Form.Group controlId="formPrioridade">
            <Form.Label className="small fw-bold text-secondary">Prioridade</Form.Label>
            <Form.Select
              size="sm"
              value={valores.PRIORIDADE || 'NORMAL'}
              onChange={(e) => onChange({ ...valores, PRIORIDADE: e.target.value })}
              required
            >
              {prioridades.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        {/* <Col md={4} xs={12}>
          <Form.Group controlId="formStatus">
            <Form.Label className="small fw-bold text-secondary">Status</Form.Label>
            <Form.Select
              size="sm"
              value={valores.STATUS || 'PENDENTE'}
              onChange={(e) => onChange({ ...valores, STATUS: e.target.value })}
              required
            >
              {statusList.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col> */}
      </Row>

      {/* SEÇÃO 4: CORPO / MENSAGEM FINAL (VISUALIZAÇÃO COMPLETA) */}
      <Form.Group controlId="formMensagem" className="mb-4">
        <Form.Label className="small fw-bold text-secondary d-flex justify-content-between">
          <span>Prévia da Mensagem Transmitida</span>
          <small className="text-muted fw-normal">Atualizado automaticamente</small>
        </Form.Label>

        {/* Textarea para observações/corpo extra */}
        <Form.Control
          as="textarea"
          rows={6}
          size="sm"
          value={valores.MENSAGEM || ''}
          onChange={(e) => onChange({ ...valores, MENSAGEM: e.target.value })}
          placeholder="A mensagem será construída automaticamente com o cabeçalho acima..."
          required
        />
      </Form.Group>

      {/* BOTÕES */}
      <div className="d-flex justify-content-end gap-2 border-top pt-3">
        {typeof onFechar === 'function' && (
          <Button variant="outline-secondary" size="sm" onClick={onFechar} className="px-3">
            Cancelar
          </Button>
        )}
        <Button type="submit" variant="primary" size="sm" className="px-4">
          {editando ? 'Salvar Alterações' : 'Enviar Mensagem'}
        </Button>
      </div>
    </Form>
  );
};

export default MensagemForm;
